'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { aiUsageApi, TokenRow } from '../../_lib/api';

export default function MyTokensPage() {
  const { t } = useTranslation();
  const tt = t.aiUsage;
  const [tokens, setTokens] = useState<TokenRow[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copyConfirmed, setCopyConfirmed] = useState(false);

  const reload = async () => {
    const res = await aiUsageApi.myTokens();
    setTokens(res.items);
  };

  useEffect(() => {
    reload();
  }, []);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const res = await aiUsageApi.createMyToken(name.trim());
      setCreatedToken(res.token);
      setName('');
      await reload();
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!confirm(tt.token.revokeConfirmBody)) return;
    await aiUsageApi.revokeMyToken(id);
    await reload();
  };

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopyConfirmed(true);
  };

  const buildFullCmd = (token: string) => {
    const base = process.env.NEXT_PUBLIC_FFCTK_INSTALL_URL ?? 'https://ffai.faradayfuture.com';
    return `curl -fsSL ${base}/install.sh | bash\nffctk login ${token}\nffctk start`;
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">{tt.token.listTitle}</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="px-3 py-2 bg-blue-600 text-white text-sm rounded"
        >
          {tt.token.createButton}
        </button>
      </div>

      {tokens.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-6 text-center">
          <div className="text-sm text-gray-700 mb-3">{tt.empty.noOwnToken}</div>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded"
          >
            {tt.empty.createFirst}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded shadow">
          <table className="w-full text-sm">
            <thead className="text-gray-500 border-b">
              <tr>
                <th className="py-3 px-4 text-left">{tt.token.columnName}</th>
                <th className="py-3 px-4 text-left">{tt.token.columnPrefix}</th>
                <th className="py-3 px-4 text-left">{tt.token.columnLastUsed}</th>
                <th className="py-3 px-4 text-left">{tt.token.columnCreated}</th>
                <th className="py-3 px-4 text-left">{tt.token.columnStatus}</th>
                <th className="py-3 px-4 text-right">{tt.token.columnActions}</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((token) => (
                <tr key={token.id} className="border-b last:border-b-0">
                  <td className="py-3 px-4">{token.name}</td>
                  <td className="py-3 px-4 font-mono text-xs">{token.prefix}...</td>
                  <td className="py-3 px-4 text-gray-500 text-xs">
                    {token.lastUsedAt ? new Date(token.lastUsedAt).toLocaleString() : '-'}
                  </td>
                  <td className="py-3 px-4 text-gray-500 text-xs">
                    {new Date(token.createdAt).toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    {token.revokedAt ? (
                      <span className="text-red-600 text-xs">{tt.token.statusRevoked}</span>
                    ) : (
                      <span className="text-green-600 text-xs">{tt.token.statusActive}</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {!token.revokedAt && (
                      <button
                        onClick={() => handleRevoke(token.id)}
                        className="text-red-600 hover:underline text-xs"
                      >
                        {tt.token.actionRevoke}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {showCreate && !createdToken && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowCreate(false)}
        >
          <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">{tt.token.createDialogTitle}</h3>
            <label className="block text-sm mb-2">{tt.token.nameLabel}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={tt.token.namePlaceholder}
              className="w-full border rounded px-3 py-2 mb-4"
              maxLength={64}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-sm border rounded hover:bg-gray-50"
              >
                {t.common?.cancel ?? 'Cancel'}
              </button>
              <button
                onClick={handleCreate}
                disabled={!name.trim() || submitting}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded disabled:opacity-50"
              >
                {tt.token.createButton}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generated token display */}
      {createdToken && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <div className="mb-3 text-green-600 font-semibold">✅ Token created</div>
            <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
              {tt.token.warningVisibleOnce}
            </div>
            <code className="block bg-gray-100 p-3 rounded text-xs font-mono break-all mb-3">
              {createdToken}
            </code>
            <button
              onClick={() => copyToClipboard(createdToken)}
              className="px-3 py-2 bg-gray-200 text-sm rounded mr-2"
            >
              {tt.token.copyToken}
            </button>
            <button
              onClick={() => copyToClipboard(buildFullCmd(createdToken))}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded"
            >
              {tt.token.copyAll}
            </button>
            <pre className="mt-3 bg-gray-900 text-gray-100 text-xs p-3 rounded whitespace-pre">
{buildFullCmd(createdToken)}
            </pre>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  if (copyConfirmed || confirm(tt.token.savedConfirm)) {
                    setCreatedToken(null);
                    setShowCreate(false);
                    setCopyConfirmed(false);
                  }
                }}
                className="px-4 py-2 bg-gray-700 text-white text-sm rounded"
              >
                {tt.token.savedClose}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
